import { ChangeDetectionStrategy, Component, HostListener, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { AuthStateService } from '../../../core/services/auth-state.service';
import { LanguageSelectorComponent } from '../../../shared/components/language-selector/language-selector.component';
import { I18nService } from '../../../core/services/i18n.service';

@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, RouterOutlet, TranslateModule, LanguageSelectorComponent],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavigationComponent {
  private readonly router = inject(Router);
  private readonly authState = inject(AuthStateService);
  private readonly i18nService = inject(I18nService);

  readonly isExpanded = signal(false);
  readonly isLanguageSelectorVisible = signal(false);
  readonly isAccountDropdownVisible = signal(false);
  readonly isMobile = signal(false);
  readonly locale = this.i18nService.locale;

  constructor() {
    this.checkScreenSize();
  }

  toggleNav(): void {
    this.isExpanded.update((v) => !v);
  }

  closeNavOnMobile(): void {
    if (this.isMobile()) {
      this.isExpanded.set(false);
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    this.isMobile.set(window.innerWidth <= 767);
  }

  async logout(): Promise<void> {
    await this.authState.logout();
    this.router.navigateByUrl('/login');
  }

  toggleLanguageSelector(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.isLanguageSelectorVisible.update((v) => !v);
    if (!this.isExpanded()) {
      this.isAccountDropdownVisible.set(false);
    }
  }

  onLanguageChanged(langCode: string): void {
    this.i18nService.setLocale(langCode as 'es' | 'en');
  }

  toggleAccountDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isAccountDropdownVisible.update((v) => !v);
    if (!this.isExpanded()) {
      this.isLanguageSelectorVisible.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    this.isLanguageSelectorVisible.set(false);
    this.isAccountDropdownVisible.set(false);
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.isLanguageSelectorVisible()) {
      const target = event.target as HTMLElement;
      const wrapper = document.querySelector('.language-selector-wrapper');
      if (wrapper && !wrapper.contains(target)) {
        this.isLanguageSelectorVisible.set(false);
      }
    }
    if (this.isAccountDropdownVisible()) {
      const target = event.target as HTMLElement;
      const wrapper = document.querySelector('.account-wrapper');
      if (wrapper && !wrapper.contains(target)) {
        this.isAccountDropdownVisible.set(false);
      }
    }
  }
}
